import {
  CurrencyDollarIcon,
  LocationMarkerIcon,
  PencilAltIcon,
  PlusIcon,
  UserIcon
} from "@heroicons/react/outline";
import {
  compact,
  filter,
  find,
  flatten,
  get,
  head,
  isEmpty,
  map,
  uniq,
  values
} from "lodash";
import React, { useMemo } from "react";
import { useHistory } from "react-router";
import { useAppearance } from "../../context/appearance";
import { useCommissionTemplates } from "../../context/commissionTemplates";
import { useContacts } from "../../context/contacts";
import { getSmartLabelFromKey } from "../../context/filteredList/FilteredListProvider";
import { useHotspotMakers } from "../../context/hotspotMakers";
import { hotspotMakerMetadata } from "../../context/hotspotMakers/HotspotMakersProvider";
import { useHotspots } from "../../context/hotspots";
import { useInstalls } from "../../context/installs";
import { useLabels as useLabelsProvider } from "../../context/labels";
import { useLocations } from "../../context/locations";
import { useWallets } from "../../context/wallets";
import { useWorkspaces } from "../../context/workspaces";
import { isActiveInstall } from "../../pages/Workspace/InstallsData/utils";
import classNames from "../../utils/classNames";
import { Entity } from "../../utils/constants";
import { Label } from "../../utils/types";
import { CurrencyKey } from "../../utils/useCurrency";
import useFilteredLabels, {
  LabelFilterKey
} from "../../utils/useFilteredLabels";
import { getColorFromId, getLabelColorStyle } from "./utils";

interface Props {
  className?: string;
  labels?: Label[];
  linkedUuid?: string;
  size?: "small" | "large";
  toggleEditing?: () => void;
  contextWalletAddresses?: string[];
  addButtonText?: string;
  editButtonText?: string;
  addButtonIcon?: React.ComponentType<any>;
  editButtonIcon?: React.ComponentType<any>;
  filterKey?: LabelFilterKey;
  showSmartFilters?: boolean;
  withoutDarkMode?: boolean;
}

const DisplayLabels: React.FC<Props> = ({
  className,
  labels,
  linkedUuid,
  size = "large",
  toggleEditing,
  contextWalletAddresses = [],
  addButtonText = "Add",
  editButtonText = "Edit",
  addButtonIcon = PlusIcon,
  editButtonIcon = PencilAltIcon,
  filterKey,
  withoutDarkMode = false
}) => {
  const history = useHistory();
  const { getHotspotMakerName } = useHotspotMakers();
  const { isDark } = useAppearance();
  const { labels: labelsMap, linkedLabels } = useLabelsProvider();
  const { toggleFilteredLabel } = useFilteredLabels();
  const { wallets } = useWallets();
  const { locations } = useLocations();
  const { currentHotspot, hotspotsMap } = useHotspots();
  const { installs } = useInstalls();
  const { contacts } = useContacts();
  const { currentWorkspaceId } = useWorkspaces();
  const { getCommissionTemplateLabel } = useCommissionTemplates();

  const selectedLabels = useMemo(() => {
    if (labels) return labels;

    let allLinkedLabels: Label[] = [];

    if (linkedUuid) {
      const isCurrentHotspot = currentHotspot?.id === linkedUuid;
      const hotspot = isCurrentHotspot
        ? currentHotspot || hotspotsMap[linkedUuid]
        : hotspotsMap[linkedUuid];

      if (hotspot?.payer) {
        allLinkedLabels.push({
          color: getColorFromId(hotspot.payer),
          name: getHotspotMakerName(hotspot.payer),
          order: 1,
          type: Entity.hotspotMaker,
          uuid: linkedUuid + hotspot.payer,
          workspaceUuid: currentWorkspaceId as string,
          link: `/hotspots/makers/${hotspot.payer}/statistics`
        } as Label);

        if (
          get(hotspotMakerMetadata, [hotspot.payer, "labels"], []).includes(
            "Remote Diagnostics"
          )
        ) {
          allLinkedLabels.push({
            color: getColorFromId("Remote Diagnostics"),
            name: "Remote Diagnostics",
            order: 2,
            type: Entity.hotspotMaker,
            uuid: linkedUuid + "diagnostics",
            workspaceUuid: currentWorkspaceId as string,
            link: `/hotspots/${linkedUuid}/status`
          } as Label);
        }
      }

      if (hotspot?.deny_list) {
        allLinkedLabels.push({
          color: getColorFromId("denylist"),
          name: "Denylist",
          order: 1,
          type: Entity.hotspot,
          uuid: "denylist",
          workspaceUuid: currentWorkspaceId as string
        } as Label);
      }
    }

    if (isEmpty(labelsMap) && isEmpty(linkedLabels)) {
      return allLinkedLabels;
    }

    // Add wallet labels associated with the given contextWalletAddresses
    const contextWalletUuids = compact(
      contextWalletAddresses.map(
        (address) => find(values(wallets), { address })?.uuid
      )
    );

    const contextWalletLinkedLabelUuids = flatten(
      contextWalletUuids.map((walletUuid) => get(linkedLabels, walletUuid, []))
    );

    allLinkedLabels = allLinkedLabels.concat(
      compact(
        contextWalletLinkedLabelUuids.map((labelUuid) => labelsMap[labelUuid])
      )
    );

    // Add labels for contacts if the contextWalletAddresses match the hnt wallet payment methods
    // This is only relevant for the labels in the wallet payment transactions (sender/receiver columns)
    // In that case there is no linkedUuid
    const matchedContacts = linkedUuid
      ? []
      : compact(
          flatten(
            contextWalletAddresses.map((hnt_address) =>
              values(contacts).filter((contact) => {
                let matchesHntWallet = false;

                filter(get(contact, "payment_methods", []), {
                  currency: CurrencyKey.hnt
                }).forEach((paymentMethod) => {
                  const parsedMetadata = JSON.parse(paymentMethod.metadata);
                  if (get(parsedMetadata, "walletAddress") === hnt_address) {
                    matchesHntWallet = true;
                  }
                });

                return matchesHntWallet;
              })
            )
          )
        );

    if (!isEmpty(matchedContacts)) {
      allLinkedLabels = matchedContacts
        .map(
          ({ uuid, name }) =>
            ({
              color: getColorFromId(uuid),
              name,
              order: 1,
              type: Entity.contact,
              uuid,
              workspaceUuid: currentWorkspaceId as string,
              link: `/contacts/${uuid}/info`,
              Icon: UserIcon
            } as Label)
        )
        .concat(allLinkedLabels);
    }

    if (linkedUuid) {
      const activeInstalls = values(installs).filter(isActiveInstall);

      // Add labels of linked item
      allLinkedLabels = allLinkedLabels.concat(
        compact(
          get(linkedLabels, linkedUuid, []).map(
            (labelUuid: string) => labelsMap[labelUuid]
          )
        )
      );

      // Add hosting label in case the linkedUuid is a contact id and there is an active install associated
      const contact = contacts[linkedUuid];
      if (!!contact) {
        const linkedCommissionLabels: string[] = [];
        let linkedCommissionInstallId: string;

        activeInstalls.forEach(({ uuid, commissions }) => {
          filter(commissions, { contact_uuid: contact.uuid }).forEach(
            (commission) => {
              const labelText = getCommissionTemplateLabel(commission);
              if (!linkedCommissionLabels.includes(labelText))
                linkedCommissionLabels.push(labelText);
              linkedCommissionInstallId = uuid;
            }
          );
        });

        const linkedLocationActiveInstalls = activeInstalls.filter(
          ({ location_uuid }) =>
            contact.linked_location_ids.includes(location_uuid)
        );

        if (!isEmpty(linkedCommissionLabels)) {
          allLinkedLabels = linkedCommissionLabels
            .map(
              (labelText) =>
                ({
                  ...getSmartLabelFromKey(labelText, Entity.contact),
                  link: `/installs/${linkedCommissionInstallId}/info`,
                  Icon: CurrencyDollarIcon
                } as Label)
            )
            .concat(allLinkedLabels);
        } else if (!isEmpty(linkedLocationActiveInstalls)) {
          allLinkedLabels = [
            {
              ...getSmartLabelFromKey("hosting", Entity.contact),
              link: `/installs/${
                head(linkedLocationActiveInstalls)?.uuid
              }/info`,
              Icon: LocationMarkerIcon
            } as Label
          ].concat(allLinkedLabels);
        }
      }

      // Create labels for contacts linked to an active install related to a hotspot or location
      let location = locations[linkedUuid];

      let install = installs[linkedUuid];

      if (!!hotspotsMap[linkedUuid]) {
        const currentHotspotInstall = find(
          values(installs).filter(isActiveInstall),
          { hotspot_uuid: linkedUuid }
        );

        if (currentHotspotInstall) install = currentHotspotInstall;
      }

      let commissionContactIds: string[] = [];

      if (!!install) {
        commissionContactIds = uniq(map(install.commissions, "contact_uuid"));

        location = locations[install.location_uuid];
      }

      const linkedLocationActiveInstalls = filter(activeInstalls, {
        location_uuid: linkedUuid
      });

      linkedLocationActiveInstalls.forEach(({ commissions }) => {
        commissions.forEach(({ contact_uuid }) => {
          if (!commissionContactIds.includes(contact_uuid))
            commissionContactIds.push(contact_uuid);
        });
      });

      const linkedLocationContactIds = location?.linked_contact_ids || [];

      const linkedContacts = compact(
        uniq(commissionContactIds.concat(linkedLocationContactIds)).map(
          (uuid) => contacts[uuid]
        )
      );

      if (!isEmpty(linkedContacts)) {
        allLinkedLabels = linkedContacts
          .map(
            ({ uuid, name }) =>
              ({
                color: getColorFromId(uuid),
                name,
                order: 1,
                type: Entity.contact,
                uuid,
                workspaceUuid: currentWorkspaceId as string,
                link: `/contacts/${uuid}/info`,
                Icon: commissionContactIds.includes(uuid)
                  ? CurrencyDollarIcon
                  : locations[linkedUuid]
                  ? UserIcon
                  : LocationMarkerIcon
              } as Label)
          )
          .concat(allLinkedLabels);
      }

      // In case of an install, add a label related to the install's status
      if (installs[linkedUuid]) {
        let installSmartLabelKey;

        if (isActiveInstall(install)) {
          installSmartLabelKey = "active install";
        } else if (!install.installed_at) {
          installSmartLabelKey = "future install";
        } else {
          installSmartLabelKey = "past install";
        }

        allLinkedLabels = [
          getSmartLabelFromKey(installSmartLabelKey, Entity.install)
        ].concat(allLinkedLabels);
      }
    }

    return allLinkedLabels;
  }, [
    contacts,
    contextWalletAddresses,
    currentHotspot,
    currentWorkspaceId,
    getCommissionTemplateLabel,
    getHotspotMakerName,
    hotspotsMap,
    installs,
    labels,
    labelsMap,
    linkedLabels,
    linkedUuid,
    locations,
    wallets
  ]);

  const ButtonIcon = useMemo(
    () => (isEmpty(selectedLabels) ? addButtonIcon : editButtonIcon),
    [addButtonIcon, editButtonIcon, selectedLabels]
  );

  const buttonText = useMemo(
    () => (isEmpty(selectedLabels) ? addButtonText : editButtonText),
    [addButtonText, editButtonText, selectedLabels]
  );

  const baseLabelClassName = useMemo(() => {
    let className = "inline-flex items-center rounded-full font-medium mx-1";

    switch (size) {
      case "small":
        className += " px-2.5 py-0.5 mt-2 text-xs";
        break;
      case "large":
      default:
        className += " px-3 py-0.5 my-2 text-sm";
    }

    if (filterKey) {
      className += " cursor-pointer";
    }

    return className;
  }, [filterKey, size]);

  return (
    <div className={classNames("labels -mx-1 flex flex-wrap", className)}>
      {selectedLabels.map(({ uuid, name, color, link, Icon }) => (
        <span
          key={uuid}
          className={baseLabelClassName}
          style={getLabelColorStyle(color, !withoutDarkMode && isDark)}
          onClick={() => {
            if (link) {
              history.push(link);
            } else if (filterKey) {
              toggleFilteredLabel(filterKey, uuid);
            }
          }}
        >
          {Icon && (
            <Icon
              className={classNames(
                size === "small" ? "-ml-0.5" : "-ml-1",
                "mr-1 h-3 w-3"
              )}
            />
          )}
          <span>{name}</span>
        </span>
      ))}

      {toggleEditing != null && (
        <button
          type="button"
          onClick={toggleEditing}
          className={classNames(
            baseLabelClassName,
            "bg-hotspotty-50 text-hotspotty-700 hover:bg-hotspotty-100 dark:bg-gray-800 dark:text-gray-200 dark:hover:text-white dark:hover:bg-gray-700"
          )}
        >
          <ButtonIcon
            className={classNames(
              size === "small" ? "-ml-0.5" : "-ml-1",
              "mr-1 h-4 w-4"
            )}
          />
          <span>{buttonText}</span>
        </button>
      )}
    </div>
  );
};

export default DisplayLabels;
